import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { AdminGiftcardTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";
import {
  computeGiftcardNgnAndUsd,
  getGiftcardDisplayDate,
  giftcardCardName,
  giftcardEcode,
  giftcardImageUrls,
  giftcardTypeLabel,
} from "./giftcardTradeMath";

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export type GiftcardTableRow = BaseRowItem & {
  raw: AdminGiftcardTransaction;
  displayDate: string;
  userName: string;
  cardName: string;
  typeLabel: string;
  country: string;
  usdLabel: string;
  ngnLabel: string;
  statusLabel: string;
  attachmentHint: string;
};

function userDisplayName(tx: AdminGiftcardTransaction): string {
  const u = tx.user;
  if (!u || typeof u !== "object") return "—";
  const profile = (u as { profile?: { firstName?: string; lastName?: string } })
    .profile;
  const f = profile?.firstName?.trim() ?? "";
  const l = profile?.lastName?.trim() ?? "";
  const name = `${f} ${l}`.trim();
  return name || "—";
}

export function buildGiftcardTableRows(
  transactions: AdminGiftcardTransaction[]
): GiftcardTableRow[] {
  return transactions.map((tx) => {
    const { ngn, usd } = computeGiftcardNgnAndUsd(tx);
    const imgs = giftcardImageUrls(tx);
    const ecode = giftcardEcode(tx);
    let attachmentHint = "—";
    if (ecode) attachmentHint = "E-code";
    else if (imgs.length > 0)
      attachmentHint = imgs.length === 1 ? "1 image" : `${imgs.length} images`;

    return {
      id: tx._id,
      raw: tx,
      displayDate: getGiftcardDisplayDate(tx),
      userName: userDisplayName(tx),
      cardName: giftcardCardName(tx),
      typeLabel: giftcardTypeLabel(tx.type),
      country: str(tx.country) || "—",
      usdLabel: usd > 0 ? `$${formatAmount(usd)}` : "—",
      ngnLabel: ngn > 0 ? `₦${formatAmount(ngn)}` : "—",
      statusLabel: str(tx.status) || "—",
      attachmentHint,
    };
  });
}

export function createGiftcardTableColumns(
  onOpen: (row: GiftcardTableRow) => void
): ColumnDef<GiftcardTableRow, unknown>[] {
  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "userName",
      header: "User",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "cardName",
      header: "Card",
      cell: ({ getValue }) => (
        <span className="text-[12px] font-semibold uppercase text-[#BEE1FB]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "typeLabel",
      header: "Type",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "country",
      header: "Country",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "usdLabel",
      header: "Face (USD)",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] text-[#E7EBEE]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "ngnLabel",
      header: "Payout (NGN)",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] font-medium text-white">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "attachmentHint",
      header: "Proof",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusBadgeClass(
            row.original.raw.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => {
        const st = String(row.original.raw.status ?? "").toUpperCase();
        const isPending = st === "PENDING" || st === "IN PROGRESS";
        return (
          <button
            type="button"
            className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button hover:border-secondary_button hover:bg-[#273037]"
            onClick={() => onOpen(row.original)}
          >
            {isPending ? "Review" : "View"}
          </button>
        );
      },
    },
  ];
}
